import AdvancedStatsHelpModal from './AdvancedStatsHelpModal';
import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Tabs, Tab } from '@mui/material';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, LineChart, Line, CartesianGrid, Tooltip, Legend } from 'recharts';
import './StatsPage.css';
import './StatsAdvanced.css';
import { 
  calculateAllAdvancedStats,
  calculatePlayerAdvancedStats,
  SHOT_COLORS 
} from '../utils/advancedStatsCalculator';
import { getPlayerDisplayName } from '../playerDisplay';  // 🆕 AJOUT

// Note: Nécessite l'installation de : npm install jspdf html2canvas
import generatePlayerPDF from './pdf/generatePDF';


// ==========================================
// COMPOSANT PRINCIPAL
// ==========================================


// Custom label avec distance ajustable
const RADIAN = Math.PI / 180;
// Arrondi intelligent (largest remainder method) pour que le total fasse 100%
const roundPercentages = (data, valueKey = 'value') => {
  const total = data.reduce((sum, d) => sum + (d[valueKey] || 0), 0);
  if (total === 0) return data.map(d => ({ ...d, pct: 0 }));
  const withRaw = data.map(d => ({
    ...d,
    pct: (d[valueKey] / total) * 100,
    floor: Math.floor((d[valueKey] / total) * 100),
    remainder: ((d[valueKey] / total) * 100) % 1,
  }));
  let remaining = 100 - withRaw.reduce((s, d) => s + d.floor, 0);
  return withRaw
    .sort((a, b) => b.remainder - a.remainder)
    .map((d, i) => ({ ...d, pct: d.floor + (i < remaining ? 1 : 0) }))
    .sort((a, b) => data.findIndex(x => x.name === a.name) - data.findIndex(x => x.name === b.name));
};

const renderCustomLabel = ({ cx, cy, midAngle, outerRadius, percent, name, fill, payload }) => {
  const radius = outerRadius + 10;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  
  // Utiliser la couleur solide du payload si disponible, sinon fill
  const solidColor = payload?.color || fill;
  
  // Labels longs sur 2 lignes : "Erreurs Adversaire", "Points Adversaire", et "Fautes ..."
  const isLongLabel = name === 'Erreurs Adversaire' || name === 'Points Adversaire' || name.startsWith('Fautes ');
  
  if (isLongLabel) {
    const anchor = x > cx ? 'start' : 'end';
    
    // Estimer la largeur du texte pour centrer le %
    const textLength = name.length * 5.5;
    const centerOffset = x > cx ? textLength / 2 : -textLength / 2;
    
    // Pour tous les labels longs : nom sur ligne 1, % centré sur ligne 2
    return (
      <text 
        fill={solidColor}
        textAnchor={anchor}
        dominantBaseline="central"
        fontSize="11px"
        fontWeight="400"
        fontFamily="Arial, sans-serif"
      >
        <tspan x={x} y={y - 6}>{name}</tspan>
        <tspan x={x + centerOffset} y={y + 7} fontSize="11px" textAnchor="middle">{payload?.pct ?? Math.round(percent * 100)}%</tspan>
      </text>
    );
  }
  
  // Labels courts sur 1 ligne
  return (
    <text 
      x={x} 
      y={y} 
      fill={solidColor}
      textAnchor={x > cx ? 'start' : 'end'} 
      dominantBaseline="central"
      fontSize="11px"
      fontWeight="400"
      fontFamily="Arial, sans-serif"
    >
      {`${name} ${payload?.pct ?? Math.round(percent * 100)}%`}
    </text>
  );
};

// Composant pour afficher les graphiques en barres par zones
const ZoneBarChart = ({ title, data, color, currentPlayer, isService }) => {
  // Reformater les labels pour forcer 2 lignes si nécessaire
  const formattedData = data.map(item => {
    let line1 = '', line2 = '';
    
    // Pour les zones (Service/Retour)
    if (item.zone === 'Ext. Égalité') { line1 = 'Ext.'; line2 = 'Égalité'; }
    else if (item.zone === 'Corps Égalité') { line1 = 'Corps'; line2 = 'Égalité'; }
    else if (item.zone === 'T Égalité') { line1 = 'T'; line2 = 'Égalité'; }
    else if (item.zone === 'T Avantage') { line1 = 'T'; line2 = 'Avantage'; }
    else if (item.zone === 'Corps Avantage') { line1 = 'Corps'; line2 = 'Avantage'; }
    else if (item.zone === 'Ext. Avantage') { line1 = 'Ext.'; line2 = 'Avantage'; }
    // Labels spécifiques à mettre sur 2 lignes
    else if (item.zone === 'Au centre') { line1 = 'Au'; line2 = 'centre'; }
    else if (item.zone === 'Passing/Lob') { line1 = 'Passing/'; line2 = 'Lob'; }
    else if (item.zone === 'Inside out') { line1 = 'Inside'; line2 = 'out'; }
    else if (item.zone === 'Inside middle') { line1 = 'Inside'; line2 = 'middle'; }
    else if (item.zone === 'Inside in') { line1 = 'Inside'; line2 = 'in'; }
    else if (item.zone === 'Long de ligne') { line1 = 'Long de'; line2 = 'ligne'; }
    else if (item.zone === 'Coup droit') { line1 = 'Coup'; line2 = 'droit'; }
    // Pour les autres, afficher sur 1 ligne
    else {
      line1 = item.zone;
      line2 = '';
    }
    
    return {
      ...item,
      zoneLine1: line1,
      zoneLine2: line2
    };
  });

  // Générer un ID unique pour le pattern basé sur la couleur
  const patternId = `pattern-bar-${color.replace('#', '')}`;

  return (
    <div className="chart-block">
      <h3 className="chart-title">{title}</h3>
      <ResponsiveContainer width="100%" height={280}>
        <BarChart
          data={formattedData}
          margin={{ top: 20, right: 0, left: 0, bottom: 50 }}
          barCategoryGap="25%"
        >
          <defs>
            {/* Pattern hachuré pour les fautes/doubles fautes */}
            <pattern
              id={patternId}
              patternUnits="userSpaceOnUse"
              width="8"
              height="8"
            >
              <rect width="8" height="8" fill={color} />
              <line x1="0" y1="0" x2="8" y2="8" stroke="white" strokeWidth="2" />
              <line x1="8" y1="0" x2="0" y2="8" stroke="white" strokeWidth="2" />
            </pattern>
          </defs>
          <XAxis 
            dataKey="zoneLine1"
            interval={0}
            tickLine={false}
            axisLine={true}
            tick={({ x, y, index }) => {
              const item = formattedData[index];
              if (!item.zoneLine2) {
                return (
                  <g>
                    <text x={x} y={y} dy={16} textAnchor="middle" fontSize={10} fill="#666">
                      {item.zoneLine1}
                    </text>
                  </g>
                );
              }
              return (
                <g>
                  <text x={x} y={y} dy={16} textAnchor="middle" fontSize={10} fill="#666">
                    {item.zoneLine1}
                  </text>
                  <text x={x} y={y} dy={28} textAnchor="middle" fontSize={10} fill="#666">
                    {item.zoneLine2}
                  </text>
                </g>
              );
            }}
            height={60}
          />
          <YAxis hide />

          <Bar 
            dataKey="main" 
            fill={color}
            barSize={22}
            name={isService ? 'Aces / Services gagnants' : 'Points gagnés'}
            label={{ 
              position: 'top', 
              fontSize: 11,
              formatter: (value) => value > 0 ? value : ''
            }}
          />
          <Bar 
            dataKey="secondary" 
            fill={`url(#${patternId})`}
            barSize={22}
            name={isService ? 'Doubles fautes' : 'Points perdus'}
            label={{ 
              position: 'top', 
              fontSize: 11,
              formatter: (value) => value > 0 ? value : ''
            }}
          />
        </BarChart>
      </ResponsiveContainer>
      
      {/* Légende personnalisée */}
      <div style={{ display: 'flex', justifyContent: 'center', gap: '20px', marginTop: '-60px', marginBottom: '40px', fontSize: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ width: '20px', height: '12px', backgroundColor: color }}></div>
          <span>{isService ? 'Aces / Services gagnants' : 'Points gagnés'}</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ 
            width: '20px', 
            height: '12px', 
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='8' height='8' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='8' height='8' fill='${encodeURIComponent(color)}'/%3E%3Cline x1='0' y1='0' x2='8' y2='8' stroke='white' stroke-width='2'/%3E%3Cline x1='8' y1='0' x2='0' y2='8' stroke='white' stroke-width='2'/%3E%3C/svg%3E")`,
            backgroundSize: '8px 8px'
          }}></div>
          <span>{isService ? 'Doubles fautes' : 'Points perdus'}</span>
        </div>
      </div>
    </div>
  );
};

// Composant pour afficher les donuts des coups décisifs
const DecisiveShotsChart = ({ data, title, subtitle }) => {
  if (data.length === 0) {
    return (
      <div className="chart-block">
        <h3 className="chart-title">{title}</h3>
        <p className="chart-subtitle">{subtitle}</p>
        <p className="chart-no-data">Aucune donnée disponible</p>
      </div>
    );
  }

  // Préparer les données pour le graphique avec préfixe "Fautes" si nécessaire
  const chartData = data.map(item => ({
    name: item.type === 'fautes' ? `Fautes ${item.label}` : item.label,
    value: item.value,
    color: SHOT_COLORS[item.label], // Couleur unie pour les labels
    isPattern: item.type === 'fautes' // Hachures pour les fautes
  }));
  // Arrondi intelligent pour que le total fasse exactement 100%
  const chartDataWithPct = roundPercentages(chartData);

  return (
    <div className="chart-block">
      <h3 className="chart-title">{title}</h3>
      <p className="chart-subtitle">{subtitle}</p>
      <ResponsiveContainer width="100%" height={280}>
        <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
          <defs>
            {/* Définir les patterns de hachures croisées pour chaque couleur */}
            {Object.entries(SHOT_COLORS).map(([shot, color]) => (
              <pattern
                key={`pattern-${shot}`}
                id={`pattern-${shot.replace(' ', '-')}`}
                patternUnits="userSpaceOnUse"
                width="8"
                height="8"
              >
                <rect width="8" height="8" fill={color} />
                {/* Hachures diagonales */}
                <line x1="0" y1="0" x2="8" y2="8" stroke="white" strokeWidth="2" />
                <line x1="8" y1="0" x2="0" y2="8" stroke="white" strokeWidth="2" />
              </pattern>
            ))}
          </defs>
          <Pie
            data={chartDataWithPct}
            cx="50%"
            cy="50%"
            labelLine={false}
            label={renderCustomLabel}
            outerRadius={85}
            innerRadius={60}
            dataKey="value"
          >
            {chartDataWithPct.map((entry, index) => {
              const fill = entry.isPattern 
                ? `url(#pattern-${entry.name.replace('Fautes ', '').replace(' ', '-')})` 
                : entry.color;
              return <Cell key={`cell-${index}`} fill={fill} />;
            })}
          </Pie>
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
};

// Composant réutilisable pour la zone de commentaires pliable
const CommentsBoxCollapsible = ({ value, onChange, blockName }) => {
  const [isOpen, setIsOpen] = React.useState(false);

  return (
    <div className="comments-section-collapsible">
      <button 
        className="comments-toggle-button"
        onClick={() => setIsOpen(!isOpen)}
      >
        <span>{isOpen ? '▼' : '▶'} Commentaires</span>
      </button>
      {isOpen && (
        <textarea
          value={value}
          onChange={onChange}
          placeholder="Ajoutez vos observations..."
          className="comments-textarea"
        />
      )}
    </div>
  );
};

// Composant pour afficher une section de comparaison
const ComparisonSection = ({ title, stats }) => {
  return (
    <div className="comparison-section">
      <h4 className="comparison-section-title">
        {title}
      </h4>
      {stats.map((stat, index) => {
        const total = stat.j1 + stat.j2;
        
        // Ne pas afficher si les deux valeurs sont à 0
        if (total === 0) return null;

        const j1Percent = total > 0 ? (stat.j1 / total) * 100 : 0;
        const j2Percent = total > 0 ? (stat.j2 / total) * 100 : 0;

        return (
          <div key={index} className="comparison-stat">
            {/* Label */}
            <div className="comparison-stat-label">
              {stat.label}
            </div>
            
            {/* Barre empilée */}
            <div className="comparison-bar-container">
              {/* Barre J1 (Vert) */}
              {stat.j1 > 0 && (
                <div 
                  className="comparison-bar j1"
                  style={{ width: `${j1Percent}%` }}
                >
                  {j1Percent > 15 && stat.j1}
                </div>
              )}
              
              {/* Barre J2 (Rouge) */}
              {stat.j2 > 0 && (
                <div 
                  className="comparison-bar j2"
                  style={{ width: `${j2Percent}%` }}
                >
                  {j2Percent > 15 && stat.j2}
                </div>
              )}
              
              {/* Valeurs à l'extérieur si barres trop petites */}
              {j1Percent > 0 && j1Percent <= 15 && (
                <span className="comparison-value-outside j1">
                  {stat.j1}
                </span>
              )}
              
              {j2Percent > 0 && j2Percent <= 15 && (
                <span className="comparison-value-outside j2">
                  {stat.j2}
                </span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

const StatsAdvanced = React.forwardRef(({ match }, ref) => {
  // 🆕 DEBUG
  console.log('🔵 StatsAdvanced monté');
  console.log('🔵 Match:', match);
  console.log('🔵 Match.id:', match?.id);
  console.log('🔵 Match.matchId:', match?.matchId);
  console.log('🔵 engineState?', !!match?.engineState);
  console.log('🔵 Points?', match?.engineState?.points?.length);
  
  const [tab, setTab] = useState(0); // Onglet set (0 = match complet)
  const [playerTab, setPlayerTab] = useState(0);
  const [helpSection, setHelpSection] = useState(null); // Onglet joueur (0 = J1, 1 = J2)
  const [matchFormat, setMatchFormat] = useState('BEST_OF_3');
  const [playerNames, setPlayerNames] = useState({ joueur1: 'Joueur 1', joueur2: 'Joueur 2' });
  const [commentsBloc1, setCommentsBloc1] = useState('');
  const [commentsBloc2, setCommentsBloc2] = useState('');
  const [commentsBloc3, setCommentsBloc3] = useState('');
  const [commentsBloc4, setCommentsBloc4] = useState('');
  const [commentsBloc5, setCommentsBloc5] = useState('');
  const [commentsBloc6, setCommentsBloc6] = useState('');
  const [showPdfModal, setShowPdfModal] = useState(false);
  const [generatingPdf, setGeneratingPdf] = useState(false);
  const isLoadingCommentsRef = useRef(false);  // 🆕 useRef pour flag IMMÉDIAT (pas de re-render)

  // Exposer openPdfModal via ref
  React.useImperativeHandle(ref, () => ({
    openPdfModal: () => {
      console.log('🎯 openPdfModal appelé depuis le parent');
      console.log('📊 État actuel showPdfModal:', showPdfModal);
      setShowPdfModal(true);
      console.log('✅ setShowPdfModal(true) exécuté');
    }
  }));

  useEffect(() => {
    if (!match) return;
    
    // Récupérer les noms des joueurs (prénoms si disponibles)
    setPlayerNames({
      joueur1: getPlayerDisplayName(match, "J1"),
      joueur2: getPlayerDisplayName(match, "J2")
    });
    
    // Récupérer le format du match
    if (match.formatMatch) {
      setMatchFormat(match.formatMatch);
    }
  }, [match]);

  // 🆕 USEEFFECT SÉPARÉ pour charger les commentaires au changement de matchId OU playerTab
  useEffect(() => {
    // 🆕 Accepter id OU matchId
    const matchIdentifier = match?.id || match?.matchId;
    if (!matchIdentifier) {
      console.log('⚠️ Pas d\'identifiant pour charger les commentaires');
      return;
    }
    
    isLoadingCommentsRef.current = true;  // 🆕 Bloquer la sauvegarde IMMÉDIATEMENT
    
    const playerCode = playerTab === 0 ? 'J1' : 'J2';
    const storageKey = `statsAdvanced_comments_${matchIdentifier}_${playerCode}`;
    
    console.log(`🔍 Chargement des commentaires pour match: ${matchIdentifier}, joueur: ${playerCode}`);
    const savedComments = localStorage.getItem(storageKey);
    
    if (savedComments) {
      try {
        const parsed = JSON.parse(savedComments);
        console.log("✅ Commentaires chargés:", parsed);
        setCommentsBloc1(parsed.bloc1 || '');
        setCommentsBloc2(parsed.bloc2 || '');
        setCommentsBloc3(parsed.bloc3 || '');
        setCommentsBloc4(parsed.bloc4 || '');
        setCommentsBloc5(parsed.bloc5 || '');
        setCommentsBloc6(parsed.bloc6 || '');
      } catch (e) {
        console.error('❌ Error loading comments:', e);
      }
    } else {
      console.log(`ℹ️ Aucun commentaire sauvegardé pour ${playerCode}`);
      // Réinitialiser à vide
      setCommentsBloc1('');
      setCommentsBloc2('');
      setCommentsBloc3('');
      setCommentsBloc4('');
      setCommentsBloc5('');
      setCommentsBloc6('');
    }
    
    // Débloquer la sauvegarde après un délai pour laisser React terminer les updates
    setTimeout(() => {
      isLoadingCommentsRef.current = false;  // 🆕 Débloquer IMMÉDIATEMENT
      console.log("🔓 Sauvegarde des commentaires débloquée");
    }, 100);
  }, [match?.id, match?.matchId, playerTab]);  // 🎯 Charger quand matchId OU playerTab change

  // Sauvegarder les commentaires dans localStorage à chaque modification
  useEffect(() => {
    // 🆕 Accepter id OU matchId
    const matchIdentifier = match?.id || match?.matchId;
    
    if (!matchIdentifier || isLoadingCommentsRef.current) {  // 🆕 Vérifier .current
      console.log("⏸️ Sauvegarde ignorée (chargement en cours ou pas d'identifiant)");
      return;  // 🆕 Ne pas sauvegarder pendant le chargement
    }
    
    const playerCode = playerTab === 0 ? 'J1' : 'J2';
    const storageKey = `statsAdvanced_comments_${matchIdentifier}_${playerCode}`;
    
    const commentsData = {
      bloc1: commentsBloc1,
      bloc2: commentsBloc2,
      bloc3: commentsBloc3,
      bloc4: commentsBloc4,
      bloc5: commentsBloc5,
      bloc6: commentsBloc6
    };
    
    console.log(`💾 Sauvegarde des commentaires pour match: ${matchIdentifier}, joueur: ${playerCode}`, commentsData);
    localStorage.setItem(storageKey, JSON.stringify(commentsData));
  }, [commentsBloc1, commentsBloc2, commentsBloc3, commentsBloc4, commentsBloc5, commentsBloc6, match?.id, match?.matchId, playerTab]);  // 🆕 Ajouter id

  // 🆕 Vérifications
  if (!match || (!match.id && !match.matchId)) {
    return (
      <div className="stats-container">
        <p className="no-match-message">
          Aucun match sélectionné
        </p>
      </div>
    );
  }
  
  if (!match.engineState?.points) {
    return (
      <div className="stats-container">
        <p className="no-match-message">
          Aucune donnée de points disponible
        </p>
      </div>
    );
  }
  
  console.log('✅ StatsAdvanced - Match valide avec', match.engineState.points.length, 'points');

  const points = match.engineState?.points || [];

  // Filtrer les points selon l'onglet set sélectionné
  const filteredPoints = tab === 0 
    ? points
    : points.filter(p => p.setNumero === tab);

  // Calculer les stats pour le joueur sélectionné
  const currentPlayer = playerTab === 0 ? 'J1' : 'J2';
  const opponent = playerTab === 0 ? 'J2' : 'J1';


  const playerStats = calculatePlayerAdvancedStats(filteredPoints, currentPlayer, opponent);
  const dataPointsWon = playerStats.pointsWon;
  const dataPointsLost = playerStats.pointsLost;

  // Vérifier si on a des données
  const hasData = dataPointsWon.length > 0 || dataPointsLost.length > 0;


  const allStats = calculateAllAdvancedStats(points, filteredPoints);
  const comparisonStats = allStats.comparison;

  // Fonction pour calculer les coups décisifs (avantages/désavantages)
  const calculateDecisiveShots = () => {
    const { j1, j2 } = comparisonStats;
    
    const advantages = {
      j1: [], // Coups à l'avantage de J1
      j2: []  // Coups à l'avantage de J2
    };

    // Points gagnants : différence positive = avantage J1
    const pointsStats = [
      { label: 'Service', diff: j1.pointsService - j2.pointsService, type: 'points' },
      { label: 'Retour', diff: j1.pointsRetour - j2.pointsRetour, type: 'points' },
      { label: 'Coup Droit', diff: j1.pointsCoupDroit - j2.pointsCoupDroit, type: 'points' },
      { label: 'Revers', diff: j1.pointsRevers - j2.pointsRevers, type: 'points' },
      { label: 'Volée', diff: j1.pointsVolee - j2.pointsVolee, type: 'points' }
    ];

    // Fautes : différence négative = avantage J1 (moins de fautes)
    const fautesStats = [
      { label: 'Service', diff: j1.fautesService - j2.fautesService, type: 'fautes' },
      { label: 'Retour', diff: j1.fautesRetour - j2.fautesRetour, type: 'fautes' },
      { label: 'Coup Droit', diff: j1.fautesCoupDroit - j2.fautesCoupDroit, type: 'fautes' },
      { label: 'Revers', diff: j1.fautesRevers - j2.fautesRevers, type: 'fautes' },
      { label: 'Volée', diff: j1.fautesVolee - j2.fautesVolee, type: 'fautes' }
    ];

    // Traiter les points gagnants
    pointsStats.forEach(stat => {
      if (stat.diff === 0) return;
      
      if (stat.diff > 0) {
        advantages.j1.push({ label: stat.label, value: stat.diff, type: 'points' });
      } else {
        advantages.j2.push({ label: stat.label, value: Math.abs(stat.diff), type: 'points' });
      }
    });

    // Traiter les fautes
    fautesStats.forEach(stat => {
      if (stat.diff === 0) return;
      
      if (stat.diff < 0) { // Moins de fautes = avantage J1
        advantages.j1.push({ label: stat.label, value: Math.abs(stat.diff), type: 'fautes' });
      } else {
        advantages.j2.push({ label: stat.label, value: stat.diff, type: 'fautes' });
      }
    });

    return advantages;
  };

  const decisiveShots = calculateDecisiveShots();

  const zoneData = allStats.zoneStats;

  const directionData = allStats.directionStats;

  const rallyLengthData = allStats.rallyLengthStats;

  // Fonction pour générer le PDF
  const handleGeneratePdf = async (playerChoice) => {
    setGeneratingPdf(true);
    setShowPdfModal(false);

    // IMPORTANT : Basculer sur "Match complet" (tab=0) pour avoir toutes les données
    setTab(0);
    await new Promise(resolve => setTimeout(resolve, 300));

    // 🆕 FONCTION HELPER : Charger les commentaires d'un joueur depuis localStorage
    const loadCommentsForPlayer = (playerCode) => {
      const storageKey = `statsAdvanced_comments_${match.matchId}_${playerCode}`;
      const savedComments = localStorage.getItem(storageKey);
      
      if (savedComments) {
        try {
          const parsed = JSON.parse(savedComments);
          console.log(`📖 Commentaires ${playerCode} chargés pour PDF:`, parsed);
          return {
            bloc1: parsed.bloc1 || '',
            bloc2: parsed.bloc2 || '',
            bloc3: parsed.bloc3 || '',
            bloc4: parsed.bloc4 || '',
            bloc5: parsed.bloc5 || '',
            bloc6: parsed.bloc6 || ''
          };
        } catch (e) {
          console.error(`❌ Erreur chargement commentaires ${playerCode}:`, e);
        }
      }
      
      console.log(`ℹ️ Aucun commentaire pour ${playerCode}`);
      return {
        bloc1: '',
        bloc2: '',
        bloc3: '',
        bloc4: '',
        bloc5: '',
        bloc6: ''
      };
    };

    try {
      if (playerChoice === 'BOTH') {
        // Générer PDF Joueur 1
        setPlayerTab(0); // Basculer sur J1
        await new Promise(resolve => setTimeout(resolve, 500)); // Attendre le rendu
        
        await generatePlayerPDF({
          match,
          playerCode: 'J1',
          playerName: playerNames.joueur1,
          opponentName: playerNames.joueur2,
          statsData: {},
          comments: loadCommentsForPlayer('J1')  // 🆕 Charger depuis localStorage
        });
        
        // Attendre entre les 2 générations
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Générer PDF Joueur 2
        setPlayerTab(1); // Basculer sur J2
        await new Promise(resolve => setTimeout(resolve, 500)); // Attendre le rendu
        
        await generatePlayerPDF({
          match,
          playerCode: 'J2',
          playerName: playerNames.joueur2,
          opponentName: playerNames.joueur1,
          statsData: {},
          comments: loadCommentsForPlayer('J2')  // 🆕 Charger depuis localStorage
        });
      } else {
        // Générer un seul PDF
        const playerCode = playerChoice;
        const playerName = playerChoice === 'J1' ? playerNames.joueur1 : playerNames.joueur2;
        const opponentName = playerChoice === 'J1' ? playerNames.joueur2 : playerNames.joueur1;
        
        // Basculer sur le bon joueur
        setPlayerTab(playerChoice === 'J1' ? 0 : 1);
        await new Promise(resolve => setTimeout(resolve, 500)); // Attendre le rendu
        
        await generatePlayerPDF({
          match,
          playerCode,
          playerName,
          opponentName,
          statsData: {},
          comments: loadCommentsForPlayer(playerCode)  // 🆕 Charger depuis localStorage
        });
      }
      
      alert('✅ PDF généré(s) avec succès !');
    } catch (error) {
      console.error('Erreur génération PDF:', error);
      alert('❌ Erreur lors de la génération du PDF');
    } finally {
      setGeneratingPdf(false);
    }
  };

  return (
    <div>

      {/* Onglets de sélection Set */}
      <Tabs 
        value={tab} 
        onChange={(_, newTab) => setTab(newTab)} 
        centered 
        sx={{ 
          mb: 1,
          '& .MuiTab-root': {
            fontSize: matchFormat === 'BEST_OF_5' ? '10px' : '11px',
            textTransform: 'lowercase',
            minHeight: '32px',
            minWidth: matchFormat === 'BEST_OF_5' ? '60px' : 'auto',
            padding: matchFormat === 'BEST_OF_5' ? '6px 8px' : '6px 16px',
            fontWeight: 600,
            color: '#666'
          },
          '& .Mui-selected': {
            color: '#1976D2 !important',
            fontWeight: 700
          },
          '& .MuiTabs-indicator': {
            backgroundColor: '#1976D2',
            height: '3px',
            bottom: '8px'
          }
        }}
      >
        <Tab label="match complet" />
        <Tab label="set 1" />
        <Tab label="set 2" />
        <Tab label="set 3" />
        {matchFormat === 'BEST_OF_5' && [
          <Tab key="set4" label="set 4" />,
          <Tab key="set5" label="set 5" />
        ]}
      </Tabs>

      {/* Header joueurs cliquable */}
      <div className="players-header">
        <div 
          className={`player-info clickable ${playerTab === 0 ? 'active' : ''}`}
          onClick={() => setPlayerTab(0)}
        >
          <div className="player-color-box player-color-j1" />
          <span className="player-name">
            {playerNames.joueur1}
          </span>
        </div>
        <div 
          className={`player-info clickable ${playerTab === 1 ? 'active' : ''}`}
          onClick={() => setPlayerTab(1)}
        >
          <span className="player-name">
            {playerNames.joueur2}
          </span>
          <div className="player-color-box player-color-j2" />
        </div>
      </div>

      {/* Section avec les 2 graphiques */}
      <div className="stats-section" id="advanced-bloc1" style={{ position: 'relative' }}>
        <button onClick={() => setHelpSection('BLOC1')} style={{ position: 'absolute', top: '8px', right: '8px', width: '22px', height: '22px', borderRadius: '50%', border: 'none', backgroundColor: '#1976D2', color: 'white', fontSize: '12px', fontWeight: '700', cursor: 'pointer', zIndex: 10, lineHeight: 1 }}>?</button>
        {!hasData ? (
          // Message unique si aucune donnée
          <div className="no-data-message">
            <p>❌ Aucune donnée disponible</p>
            <p>
              {tab === 0 
                ? "Aucun point enregistré pour ce match"
                : `Aucun point enregistré pour le set ${tab}`
              }
            </p>
          </div>
        ) : (
          <>
            {/* Graphique 1 : Points gagnés (Donut) - Affiché seulement si données */}
            {dataPointsWon.length > 0 && (
              <div className="chart-block">
                <h3 className="chart-title">
                  Répartition des points gagnés
                </h3>
                <p className="chart-subtitle">
                  (Points gagnants du joueur + fautes de l'adversaire)
                </p>
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                    <Pie
                      data={roundPercentages(dataPointsWon)}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={renderCustomLabel}
                      outerRadius={85}
                      innerRadius={60}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {roundPercentages(dataPointsWon).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Ligne de séparation - Affichée seulement si les 2 graphiques ont des données */}
            {dataPointsWon.length > 0 && dataPointsLost.length > 0 && (
              <div className="chart-separator" />
            )}

            {/* Graphique 2 : Points perdus (Donut) - Affiché seulement si données */}
            {dataPointsLost.length > 0 && (
              <div className="chart-block">
                <h3 className="chart-title">
                  Répartition des points perdus
                </h3>
                <p className="chart-subtitle">
                  (Fautes du joueur + points gagnants de l'adversaire)
                </p>
                <ResponsiveContainer width="100%" height={280}>
                  <PieChart margin={{ top: 0, right: 0, bottom: 0, left: 0 }}>
                    <Pie
                      data={roundPercentages(dataPointsLost)}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={renderCustomLabel}
                      outerRadius={85}
                      innerRadius={60}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {roundPercentages(dataPointsLost).map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>
              </div>
            )}

            {/* Zone de commentaires */}
            <CommentsBoxCollapsible 
              value={commentsBloc1}
              onChange={(e) => setCommentsBloc1(e.target.value)}
              blockName="bloc1"
            />
          </>
        )}
      </div>

      {/* Bloc 2 : Graphique de comparaison */}
      {hasData && (
        <div className="stats-section comparison-block" id="advanced-bloc2" style={{ position: 'relative' }}>
          <button onClick={() => setHelpSection('BLOC2')} style={{ position: 'absolute', top: '8px', right: '8px', width: '22px', height: '22px', borderRadius: '50%', border: 'none', backgroundColor: '#FF9800', color: 'white', fontSize: '12px', fontWeight: '700', cursor: 'pointer', zIndex: 10, lineHeight: 1 }}>?</button>
          <h3 className="chart-title">
            Comparaison de chaque coup
          </h3>

          {/* Échelle supérieure (J1 - Vert) */}
          <div className="comparison-scale top">
            <span>0%</span>
            <span>20%</span>
            <span>40%</span>
            <span>60%</span>
            <span>80%</span>
            <span>100%</span>
          </div>

          {/* Ligne de séparation supérieure */}
          <div className="comparison-scale-line top">
            <div className="scale-tick top" style={{ left: '0%' }}></div>
            <div className="scale-tick top" style={{ left: '20%' }}></div>
            <div className="scale-tick top" style={{ left: '40%' }}></div>
            <div className="scale-tick top" style={{ left: '60%' }}></div>
            <div className="scale-tick top" style={{ left: '80%' }}></div>
            <div className="scale-tick top" style={{ left: '100%' }}></div>
          </div>

          {/* Section Points gagnants */}
          <ComparisonSection 
            title="Points gagnants"
            stats={[
              { label: 'Points au service', j1: comparisonStats.j1.pointsService, j2: comparisonStats.j2.pointsService },
              { label: 'Points en retour', j1: comparisonStats.j1.pointsRetour, j2: comparisonStats.j2.pointsRetour },
              { label: 'Points en coup droit', j1: comparisonStats.j1.pointsCoupDroit, j2: comparisonStats.j2.pointsCoupDroit },
              { label: 'Points en revers', j1: comparisonStats.j1.pointsRevers, j2: comparisonStats.j2.pointsRevers },
              { label: 'Points à la volée', j1: comparisonStats.j1.pointsVolee, j2: comparisonStats.j2.pointsVolee }
            ]}
          />

          {/* Mini-séparateur */}
          <div className="comparison-separator" />

          {/* Section Fautes */}
          <ComparisonSection 
            title="Fautes"
            stats={[
              { label: 'Fautes au service', j1: comparisonStats.j1.fautesService, j2: comparisonStats.j2.fautesService },
              { label: 'Fautes en retour', j1: comparisonStats.j1.fautesRetour, j2: comparisonStats.j2.fautesRetour },
              { label: 'Fautes en coup droit', j1: comparisonStats.j1.fautesCoupDroit, j2: comparisonStats.j2.fautesCoupDroit },
              { label: 'Fautes en revers', j1: comparisonStats.j1.fautesRevers, j2: comparisonStats.j2.fautesRevers },
              { label: 'Fautes à la volée', j1: comparisonStats.j1.fautesVolee, j2: comparisonStats.j2.fautesVolee }
            ]}
          />

          {/* Ligne de séparation inférieure */}
          <div className="comparison-scale-line bottom">
            <div className="scale-tick bottom" style={{ left: '0%' }}></div>
            <div className="scale-tick bottom" style={{ left: '20%' }}></div>
            <div className="scale-tick bottom" style={{ left: '40%' }}></div>
            <div className="scale-tick bottom" style={{ left: '60%' }}></div>
            <div className="scale-tick bottom" style={{ left: '80%' }}></div>
            <div className="scale-tick bottom" style={{ left: '100%' }}></div>
          </div>

          {/* Échelle inférieure (J2 - Rouge, inversée) */}
          <div className="comparison-scale bottom">
            <span>100%</span>
            <span>80%</span>
            <span>60%</span>
            <span>40%</span>
            <span>20%</span>
            <span>0%</span>
          </div>

          {/* Zone de commentaires */}
          <CommentsBoxCollapsible 
            value={commentsBloc2}
            onChange={(e) => setCommentsBloc2(e.target.value)}
            blockName="bloc2"
          />
        </div>
      )}

      {/* Bloc 3 : Analyse des coups décisifs */}
      {hasData && (
        <div className="stats-section comparison-block" id="advanced-bloc3" style={{ position: 'relative' }}>
          <button onClick={() => setHelpSection('BLOC3')} style={{ position: 'absolute', top: '8px', right: '8px', width: '22px', height: '22px', borderRadius: '50%', border: 'none', backgroundColor: '#388E3C', color: 'white', fontSize: '12px', fontWeight: '700', cursor: 'pointer', zIndex: 10, lineHeight: 1 }}>?</button>
          {/* Donut 1 : Coups expliquant la victoire */}
          <DecisiveShotsChart 
            data={playerTab === 0 ? decisiveShots.j1 : decisiveShots.j2}
            title={
              <>
                Coups expliquant la victoire{' '}
                <span style={{ fontSize: '12px', fontWeight: '400' }}>(ou limitant la défaite)</span>
              </>
            }
            subtitle={`(Coups à l'avantage de ${playerTab === 0 ? playerNames.joueur1 : playerNames.joueur2})`}
          />

          {/* Ligne de séparation */}
          <div className="chart-separator" />

          {/* Donut 2 : Coups expliquant la défaite */}
          <DecisiveShotsChart 
            data={playerTab === 0 ? decisiveShots.j2 : decisiveShots.j1}
            title={
              <>
                Coups expliquant la défaite{' '}
                <span style={{ fontSize: '12px', fontWeight: '400' }}>(ou limitant la victoire)</span>
              </>
            }
            subtitle={`(Coups à l'avantage de ${playerTab === 0 ? playerNames.joueur2 : playerNames.joueur1})`}
          />

          {/* Zone de commentaires */}
          <CommentsBoxCollapsible 
            value={commentsBloc3}
            onChange={(e) => setCommentsBloc3(e.target.value)}
            blockName="bloc3"
          />
        </div>
      )}

      {/* Bloc 4 : Statistiques par zones */}
      {hasData && (
        <div className="stats-section comparison-block" id="advanced-bloc4" style={{ position: 'relative' }}>
          <button onClick={() => setHelpSection('BLOC4')} style={{ position: 'absolute', top: '8px', right: '8px', width: '22px', height: '22px', borderRadius: '50%', border: 'none', backgroundColor: '#7B1FA2', color: 'white', fontSize: '12px', fontWeight: '700', cursor: 'pointer', zIndex: 10, lineHeight: 1 }}>?</button>
          {/* Graphique 1 : Services par zones */}
          <ZoneBarChart 
            title="Aces / Services gagnants et doubles fautes par zones"
            data={zoneData.zoneLabels.map((label, idx) => ({
              zone: label,
              main: playerTab === 0 ? zoneData.serviceStats.j1.aces[idx] : zoneData.serviceStats.j2.aces[idx],
              secondary: playerTab === 0 ? zoneData.serviceStats.j1.doubleFautes[idx] : zoneData.serviceStats.j2.doubleFautes[idx]
            }))}
            color={SHOT_COLORS['Service']}
            playerNames={playerNames}
            currentPlayer={playerTab === 0 ? playerNames.joueur1 : playerNames.joueur2}
            isService={true}
          />

          {/* Ligne de séparation */}
          <div className="chart-separator" />

          {/* Graphique 2 : Retours par zones */}
          <ZoneBarChart 
            title="Points et fautes en retour par zones"
            data={zoneData.zoneLabels.map((label, idx) => ({
              zone: label,
              main: playerTab === 0 ? zoneData.retourStats.j1.points[idx] : zoneData.retourStats.j2.points[idx],
              secondary: playerTab === 0 ? zoneData.retourStats.j1.fautes[idx] : zoneData.retourStats.j2.fautes[idx]
            }))}
            color={SHOT_COLORS['Retour']}
            playerNames={playerNames}
            currentPlayer={playerTab === 0 ? playerNames.joueur1 : playerNames.joueur2}
            isService={false}
          />

          {/* Zone de commentaires */}
          <CommentsBoxCollapsible 
            value={commentsBloc4}
            onChange={(e) => setCommentsBloc4(e.target.value)}
            blockName="bloc4"
          />
        </div>
      )}

      {/* Bloc 5 : Statistiques par direction */}
      {hasData && (
        <div className="stats-section comparison-block" id="advanced-bloc5" style={{ position: 'relative' }}>
          <button onClick={() => setHelpSection('BLOC5')} style={{ position: 'absolute', top: '8px', right: '8px', width: '22px', height: '22px', borderRadius: '50%', border: 'none', backgroundColor: '#C62828', color: 'white', fontSize: '12px', fontWeight: '700', cursor: 'pointer', zIndex: 10, lineHeight: 1 }}>?</button>
          {/* Graphique 1 : Coup droit par direction */}
          <ZoneBarChart 
            title="Points et fautes en coup droit par direction"
            data={directionData.coupDroitDirections.map(dir => ({
              zone: dir.label,
              main: playerTab === 0 
                ? directionData.coupDroitStats.j1.points[dir.code] || 0
                : directionData.coupDroitStats.j2.points[dir.code] || 0,
              secondary: playerTab === 0
                ? directionData.coupDroitStats.j1.fautes[dir.code] || 0
                : directionData.coupDroitStats.j2.fautes[dir.code] || 0
            }))}
            color={SHOT_COLORS['Coup Droit']}
            playerNames={playerNames}
            currentPlayer={playerTab === 0 ? playerNames.joueur1 : playerNames.joueur2}
            isService={false}
          />

          {/* Ligne de séparation */}
          <div className="chart-separator" />

          {/* Graphique 2 : Revers par direction */}
          <ZoneBarChart 
            title="Points et fautes en revers par direction"
            data={directionData.reversDirections.map(dir => ({
              zone: dir.label,
              main: playerTab === 0 
                ? directionData.reversStats.j1.points[dir.code] || 0
                : directionData.reversStats.j2.points[dir.code] || 0,
              secondary: playerTab === 0
                ? directionData.reversStats.j1.fautes[dir.code] || 0
                : directionData.reversStats.j2.fautes[dir.code] || 0
            }))}
            color={SHOT_COLORS['Revers']}
            playerNames={playerNames}
            currentPlayer={playerTab === 0 ? playerNames.joueur1 : playerNames.joueur2}
            isService={false}
          />

          {/* Ligne de séparation */}
          <div className="chart-separator" />

          {/* Graphique 3 : Volée par direction */}
          <ZoneBarChart 
            title="Points et fautes à la volée par direction"
            data={directionData.volleeDirections.map(dir => ({
              zone: dir.label,
              main: playerTab === 0 
                ? directionData.volleeStats.j1.points[dir.code] || 0
                : directionData.volleeStats.j2.points[dir.code] || 0,
              secondary: playerTab === 0
                ? directionData.volleeStats.j1.fautes[dir.code] || 0
                : directionData.volleeStats.j2.fautes[dir.code] || 0
            }))}
            color={SHOT_COLORS['Volée']}
            playerNames={playerNames}
            currentPlayer={playerTab === 0 ? playerNames.joueur1 : playerNames.joueur2}
            isService={false}
          />

          {/* Zone de commentaires */}
          <CommentsBoxCollapsible 
            value={commentsBloc5}
            onChange={(e) => setCommentsBloc5(e.target.value)}
            blockName="bloc5"
          />
        </div>
      )}

      {/* Bloc 6 : Statistiques par longueur de rallye */}
      {hasData && (
        <div className="stats-section comparison-block" id="advanced-bloc6" style={{ position: 'relative' }}>
          <button
            onClick={() => setHelpSection('BLOC6')}
            style={{
              position: 'absolute', top: '8px', right: '8px',
              width: '22px', height: '22px', borderRadius: '50%',
              border: 'none', backgroundColor: '#0277BD', color: 'white',
              fontSize: '12px', fontWeight: '700', cursor: 'pointer',
              zIndex: 10, lineHeight: 1,
            }}
          >?</button>

          {/* Courbe traits droits */}
          <h3 className="chart-title" style={{ marginTop: '12px' }}>
            Points gagnés / perdus selon la longueur du rallye
          </h3>
          <ResponsiveContainer width="100%" height={300}>
            {(() => {
              const rallyData = rallyLengthData.categories.map(cat => ({
                name: cat.label,
                Gagnés: playerTab === 0
                  ? rallyLengthData.stats.j1.pointsGagnes[cat.code] || 0
                  : rallyLengthData.stats.j2.pointsGagnes[cat.code] || 0,
                Perdus: playerTab === 0
                  ? rallyLengthData.stats.j1.pointsPerdus[cat.code] || 0
                  : rallyLengthData.stats.j2.pointsPerdus[cat.code] || 0,
              }));

              // Label dynamique : la valeur haute va au-dessus, la basse en dessous
              // En cas d'égalité : Gagnés au-dessus, Perdus en dessous
              const renderRallyLabel = (dataKey, color) => (props) => {
                const { x, y, value, index } = props;
                const point = rallyData[index];
                if (point === undefined || value === null) return null;
                const isGagne = dataKey === 'Gagnés';
                const other = isGagne ? point.Perdus : point.Gagnés;
                // Si égaux : Gagnés forcé au-dessus, Perdus forcé en dessous
                const goAbove = value > other || (value === other && isGagne);
                const dy = goAbove ? -14 : 18;
                return (
                  <text
                    x={x}
                    y={y + dy}
                    textAnchor="middle"
                    fill={color}
                    fontSize={12}
                    fontWeight="bold"
                  >
                    {value}
                  </text>
                );
              };

              return (
                <LineChart data={rallyData} margin={{ top: 30, right: 30, left: -30, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} />
                  <Tooltip />
                  <Legend />
                  <Line
                    type="linear"
                    dataKey="Gagnés"
                    stroke="#4CAF50"
                    strokeWidth={2.5}
                    dot={{ r: 5, fill: '#4CAF50' }}
                    activeDot={{ r: 7 }}
                    label={renderRallyLabel('Gagnés', '#4CAF50')}
                  />
                  <Line
                    type="linear"
                    dataKey="Perdus"
                    stroke="#F44336"
                    strokeWidth={2.5}
                    dot={{ r: 5, fill: '#F44336' }}
                    activeDot={{ r: 7 }}
                    label={renderRallyLabel('Perdus', '#F44336')}
                  />
                </LineChart>
              );
            })()}
          </ResponsiveContainer>

          {/* Zone de commentaires */}
          <CommentsBoxCollapsible 
            value={commentsBloc6}
            onChange={(e) => setCommentsBloc6(e.target.value)}
            blockName="bloc6"
          />
        </div>
      )}

      {/* Modal de sélection PDF */}
      {showPdfModal && ReactDOM.createPortal(
        <div className="pdf-modal-overlay" onClick={() => setShowPdfModal(false)}>
          <div className="pdf-modal" onClick={(e) => e.stopPropagation()}>
            <h2>Générer un rapport PDF</h2>
            <p>Choisissez le(s) joueur(s) pour le rapport :</p>
            <div className="pdf-modal-buttons">
              <button 
                className="pdf-modal-button"
                onClick={() => handleGeneratePdf('J1')}
                disabled={generatingPdf}
              >
                📊 PDF {playerNames.joueur1}
              </button>
              <button 
                className="pdf-modal-button"
                onClick={() => handleGeneratePdf('J2')}
                disabled={generatingPdf}
              >
                📊 PDF {playerNames.joueur2}
              </button>
              <button 
                className="pdf-modal-button pdf-modal-button-both"
                onClick={() => handleGeneratePdf('BOTH')}
                disabled={generatingPdf}
              >
                📊📊 Les 2 PDF
              </button>
            </div>
            <button 
              className="pdf-modal-close"
              onClick={() => setShowPdfModal(false)}
            >
              Annuler
            </button>
          </div>
        </div>,
        document.body
      )}
      {helpSection && <AdvancedStatsHelpModal section={helpSection} onClose={() => setHelpSection(null)} />}
    </div>
  );
});

export default StatsAdvanced;